import { useCallback } from "react";
import { selectAppState, setAppState } from "../slices/appStateSlice";
import { AppState } from "../types/mainTypes";
import { debugTrace, shouldTraceLightingDebug } from "../utils/debug/debugTrace";
import { useAppDispatch, useAppStore } from "./utilsHooks/storeHooks";

/**
 * Wake callback for react-idle-timer's `onAction`: any genuine interaction must immediately
 * brighten the fridge back from the inactive (idle → StandBy + orderStep1) welcome.
 * react-idle-timer only flips presence on a transition (and throttles it), so wake directly on
 * the first interaction; the lighting and the screen both follow `appState`, so they come back
 * together. Gated to the idle→active edge so it does not re-dispatch while already active. The
 * idle timer still owns the DIM (going IDLE on inactivity); this only governs the WAKE.
 *
 * Reads appState from the store at invocation time — deliberately NO useAppSelector
 * subscription, so the host component does not re-render on every idle/active edge.
 */
export const useIdleWakeAction = () => {
	const store = useAppStore();
	const dispatch = useAppDispatch();

	return useCallback(
		(event?: Event) => {
			if (selectAppState(store.getState()) === AppState.IDLE) {
				dispatch(setAppState(AppState.ACTIVE));
			}
			// TEMP diagnostic (master tablet + ?lightingDebug=true only): names the event that keeps
			// registering as activity. Remove with stateDebugMiddleware.
			if (shouldTraceLightingDebug()) {
				debugTrace(`[state-debug] idle-action event=${event?.type ?? "?"}`);
			}
		},
		[store, dispatch],
	);
};
